/* eslint-disable react/require-default-props -- defaults via destructuring; no defaultProps (React 19) */
import React, { useState, useMemo, useCallback, useId } from 'react';
import PropTypes from 'prop-types';
import Typography from '../Typography';
import Button from '../Button';
import { Input } from '../Input';
import { FlexRow, FlexColumn } from '../GridsAndLayouts/GridsAndLayouts';
import { ChevronDownIcon, CloseIcon, CheckIcon, SearchIcon } from '../Icons';

const Multiselect = ({
  DropdownComponent,
  label,
  options,
  value,
  defaultValue,
  onChange,
  showCheckboxes = false,
  maxVisibleChips = 2,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  searchable = false,
  disabled = false,
  error = false,
  align = 'left',
  className = '',
  id,
  onClose,
}) => {
  const generatedId = useId();
  const controlId = id || generatedId;
  const listboxId = `${controlId}-listbox`;
  const searchInputId = `${controlId}-search`;

  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue || []);
  const selectedValues = isControlled ? value : internalValue;
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState('');

  const setSelectedValues = useCallback(
    (next) => {
      if (!isControlled) {
        setInternalValue(next);
      }

      if (onChange) {
        onChange(next);
      }
    },
    [isControlled, onChange]
  );

  const optionMap = useMemo(() => {
    const map = new Map();
    options.forEach((option) => {
      map.set(String(option.value), option);
    });
    return map;
  }, [options]);

  const isSelected = useCallback(
    (optionValue) => selectedValues.some((selectedValue) => String(selectedValue) === String(optionValue)),
    [selectedValues]
  );

  const toggleValue = useCallback(
    (optionValue) => {
      if (disabled) return;

      const normalizedValue = String(optionValue);
      const nextValues = isSelected(optionValue)
        ? selectedValues.filter((selectedValue) => String(selectedValue) !== normalizedValue)
        : [...selectedValues, optionValue];

      setSelectedValues(nextValues);
    },
    [disabled, isSelected, selectedValues, setSelectedValues]
  );

  const removeValue = useCallback(
    (event, optionValue) => {
      event.stopPropagation();

      if (disabled) return;

      setSelectedValues(selectedValues.filter((selectedValue) => String(selectedValue) !== String(optionValue)));

      if (!menuOpen) {
        onClose?.();
      }
    },
    [disabled, menuOpen, onClose, selectedValues, setSelectedValues]
  );

  const handleDropdownOpen = useCallback(() => {
    setMenuOpen(true);
  }, []);

  const handleDropdownClose = useCallback(() => {
    setMenuOpen(false);
    setQuery('');
    onClose?.();
  }, [onClose]);

  const filteredOptions = useMemo(() => {
    if (!searchable || !query.trim()) {
      return options;
    }
    const q = query.trim().toLowerCase();
    return options.filter((opt) => {
      const labelStr = typeof opt.label === 'string' ? opt.label : '';
      const subStr = typeof opt.subtitle === 'string' ? opt.subtitle : '';
      return `${labelStr} ${subStr}`.toLowerCase().includes(q);
    });
  }, [options, query, searchable]);

  const selectedChips = selectedValues.map((selectedValue) => optionMap.get(String(selectedValue))).filter(Boolean);
  const visibleChips = selectedChips.slice(0, maxVisibleChips);
  const overflowCount = Math.max(0, selectedChips.length - maxVisibleChips);

  const trigger = (
    <FlexRow
      className={`ds-dropdown-multiselect__field ${disabled ? 'ds-dropdown-multiselect__field--disabled' : ''} ${
        menuOpen ? 'ds-dropdown-multiselect__field--open' : ''
      } ${error ? 'ds-dropdown-multiselect__field--error' : ''}`.trim()}
      id={controlId}
      align="center"
    >
      {label && (
        <Typography
          component="span"
          variant="body2"
          color="muted"
          className="ds-dropdown-multiselect__label"
          id={`${controlId}-label`}
        >
          {label}
        </Typography>
      )}
      <FlexRow className="ds-dropdown-multiselect__value" align="center" fullWidth={false}>
        {selectedChips.length === 0 && (
          <Typography component="span" variant="body1" className="ds-dropdown-multiselect__placeholder">
            {placeholder}
          </Typography>
        )}
        {selectedChips.length > 0 && (
          <FlexRow className="ds-dropdown-multiselect__chips" align="center" fullWidth>
            {visibleChips.map((option) => (
              <FlexRow
                key={String(option.value)}
                className="ds-dropdown-multiselect__chip"
                align="center"
                inline
                fullWidth={false}
              >
                <Typography
                  component="span"
                  variant="body2"
                  fontWeight="medium"
                  className="ds-dropdown-multiselect__chip-text"
                >
                  {option.label}
                </Typography>
                <Button
                  variant="tertiary"
                  size="sm"
                  type="button"
                  className="ds-dropdown-multiselect__chip-remove"
                  icon={<CloseIcon size={12} strokeWidth={1.2} />}
                  iconPosition="right"
                  disabled={disabled}
                  aria-label={`Remove ${option.label}`}
                  onClick={(event) => removeValue(event, option.value)}
                />
              </FlexRow>
            ))}
            {overflowCount > 0 && (
              <Typography
                component="span"
                variant="body2"
                fontWeight="medium"
                className="ds-dropdown-multiselect__overflow"
              >
                +{overflowCount}
              </Typography>
            )}
          </FlexRow>
        )}
      </FlexRow>
      <FlexRow className="ds-dropdown-multiselect__actions" align="center" inline fullWidth={false} gap={2}>
        <FlexRow
          className={`ds-dropdown-multiselect__chevron ${menuOpen ? 'ds-dropdown-multiselect__chevron--open' : ''}`}
          align="center"
          justify="center"
          inline
          fullWidth={false}
          aria-hidden
        >
          <ChevronDownIcon />
        </FlexRow>
      </FlexRow>
    </FlexRow>
  );

  const rootClass = ['ds-dropdown--multiselect', searchable && 'ds-dropdown--multiselect--searchable', className]
    .filter(Boolean)
    .join(' ');

  return (
    <DropdownComponent
      className={rootClass.trim()}
      trigger={trigger}
      align={align}
      disabled={disabled}
      onOpen={handleDropdownOpen}
      onClose={handleDropdownClose}
    >
      {searchable && (
        <Typography component="label" variant="body1" className="ds-dropdown-select__search" htmlFor={searchInputId}>
          <FlexRow
            className="ds-dropdown-select__search-icon"
            align="center"
            justify="center"
            inline
            fullWidth={false}
            aria-hidden
          >
            <SearchIcon size={14} />
          </FlexRow>
          <Input
            id={searchInputId}
            type="search"
            className="ds-dropdown-select__search-input"
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            aria-label={searchPlaceholder}
          />
        </Typography>
      )}
      <FlexColumn
        id={listboxId}
        className="ds-dropdown-multiselect__list"
        role="listbox"
        aria-multiselectable
        aria-labelledby={label ? `${controlId}-label` : undefined}
        align="start"
        fullWidth
      >
        {filteredOptions.map((option) => {
          const selected = isSelected(option.value);

          return (
            <FlexRow
              key={String(option.value)}
              role="option"
              aria-selected={selected}
              className={`ds-dropdown-multiselect__option ${
                showCheckboxes ? 'ds-dropdown-multiselect__option--checkbox' : ''
              } ${selected ? 'ds-dropdown-multiselect__option--selected' : ''}`.trim()}
              onClick={() => toggleValue(option.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  toggleValue(option.value);
                }
              }}
              tabIndex={0}
              align="start"
            >
              {showCheckboxes && (
                <FlexRow
                  className="ds-dropdown-multiselect__checkbox-wrap"
                  align="center"
                  justify="center"
                  inline
                  fullWidth={false}
                >
                  <FlexRow
                    className={[
                      'ds-dropdown-multiselect__checkbox-indicator',
                      selected && 'ds-dropdown-multiselect__checkbox-indicator--checked',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    align="center"
                    justify="center"
                    inline
                    fullWidth={false}
                    aria-hidden
                  >
                    {selected && <CheckIcon size={12} strokeWidth={2.4} className="ds-icon--color-white" />}
                  </FlexRow>
                </FlexRow>
              )}
              {option.avatar != null && (
                <FlexRow
                  className="ds-dropdown-multiselect__option-avatar"
                  align="center"
                  justify="center"
                  inline
                  fullWidth={false}
                >
                  {option.avatar}
                </FlexRow>
              )}
              <FlexColumn className="ds-dropdown-multiselect__option-text" align="start" fullWidth>
                <Typography
                  component="span"
                  variant="body1"
                  fontWeight="medium"
                  className="ds-dropdown-multiselect__option-label"
                >
                  {option.label}
                </Typography>
                {option.subtitle != null && option.subtitle !== '' && (
                  <Typography
                    component="span"
                    variant="body2"
                    color="muted"
                    className="ds-dropdown-multiselect__option-subtitle"
                  >
                    {option.subtitle}
                  </Typography>
                )}
              </FlexColumn>
            </FlexRow>
          );
        })}
      </FlexColumn>
    </DropdownComponent>
  );
};

Multiselect.propTypes = {
  DropdownComponent: PropTypes.elementType,
  label: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.node.isRequired,
      subtitle: PropTypes.node,
      avatar: PropTypes.node,
    })
  ).isRequired,
  value: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  defaultValue: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  onChange: PropTypes.func,
  showCheckboxes: PropTypes.bool,
  maxVisibleChips: PropTypes.number,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  align: PropTypes.oneOf(['left', 'right', 'center']),
  className: PropTypes.string,
  id: PropTypes.string,
  onClose: PropTypes.func,
  searchable: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
};

export default Multiselect;
